/**
 * Static demo of the real PracticeTest UI — no auth, no Appwrite, no questions loading.
 * Used only at /preview/practice (embedded as an iframe on the marketing site).
 */
import { useState } from "react";
import {
  ArrowLeft, Pause, Play, Eye, EyeOff, Maximize,
  Bookmark, BookmarkCheck, Flag, Undo2, Info,
  Lightbulb, CheckCircle2, ChevronLeft, ChevronRight,
  ChevronDown, Calculator, X, Bot, PanelLeftClose, PanelLeft,
} from "lucide-react";

const DEMO_PASSAGE =
  "A student is analyzing the cost of a school fundraiser. The school sells two types of items: notebooks for $3 each and pens for $1 each. The student wants to spend exactly $15 and buy a total of 9 items.";

const DEMO_QUESTION = {
  question: "Which system of equations correctly represents this situation, where n is the number of notebooks and p is the number of pens?",
  options: [
    { id: "A", text: "n + p = 15 and 3n + p = 9", isCorrect: false },
    { id: "B", text: "n + p = 9 and 3n + p = 15", isCorrect: true },
    { id: "C", text: "3n + p = 9 and n + p = 15", isCorrect: false },
    { id: "D", text: "n + p = 9 and n + 3p = 15", isCorrect: false },
  ],
  explanation:
    "Let n = notebooks and p = pens. Total items: n + p = 9. Total cost: 3n + p = 15. Option B matches both equations correctly.",
};

const QUESTIONS_GRID = Array.from({ length: 10 }, (_, i) => i + 1);

export function PreviewPracticePage() {
  const [selected, setSelected] = useState<string | null>(null);
  const [checked, setChecked] = useState(false);
  const [timerPaused, setTimerPaused] = useState(false);
  const [showTimer, setShowTimer] = useState(true);
  const [showExplanation, setShowExplanation] = useState(false);
  const [sidebarHidden, setSidebarHidden] = useState(false);
  const [marked, setMarked] = useState(false);

  const currentQ = 6;
  const totalQ = 10;

  const handleCheck = () => {
    if (selected) {
      setChecked(true);
      setShowExplanation(true);
    }
  };

  const selectedOption = DEMO_QUESTION.options.find((o) => o.id === selected);
  const isCorrect = selectedOption?.isCorrect;

  return (
    <div
      className="min-h-screen flex flex-col"
      style={{ fontFamily: "'Inter', 'Segoe UI', system-ui, sans-serif", background: "var(--background, #fff)", color: "var(--foreground, #0f172a)" }}
    >
      {/* ── Top Header ── */}
      <header
        style={{ height: 56, borderBottom: "1px solid #e2e8f0", background: "#fff", display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0 16px", position: "sticky", top: 0, zIndex: 30 }}
      >
        {/* Left */}
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <button style={ghostBtn}>
            <ArrowLeft size={20} />
          </button>
          <div>
            <div style={{ fontWeight: 600, fontSize: 17 }}>Practice Test</div>
            <button style={{ fontSize: 13, color: "#64748b", display: "flex", alignItems: "center", gap: 4, background: "none", border: "none", cursor: "pointer", padding: 0 }}>
              Directions <ChevronDown size={12} />
            </button>
          </div>
        </div>

        {/* Right */}
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {/* Timer controls */}
          <button style={outlineBtn} onClick={() => setTimerPaused((p) => !p)}>
            {timerPaused ? <Play size={16} /> : <Pause size={16} />}
          </button>
          <button style={outlineBtn} onClick={() => setShowTimer((p) => !p)}>
            {showTimer ? (
              <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <Eye size={16} />
                <span style={{ fontFamily: "monospace", fontSize: 13 }}>2:34</span>
              </span>
            ) : (
              <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <EyeOff size={16} /> Show
              </span>
            )}
          </button>

          {/* Sidebar toggle */}
          <button style={ghostBtn} onClick={() => setSidebarHidden((p) => !p)} title={sidebarHidden ? "Show Sidebar" : "Hide Sidebar"}>
            {sidebarHidden ? <PanelLeft size={20} /> : <PanelLeftClose size={20} />}
          </button>

          {/* Quick icons */}
          <div style={{ display: "flex", alignItems: "center", gap: 8, paddingLeft: 12, borderLeft: "1px solid #e2e8f0", marginLeft: 4 }}>
            <button style={ghostBtn} title="Calculator">
              <Calculator size={20} />
            </button>
            <button style={ghostBtn} title="Fullscreen">
              <Maximize size={20} />
            </button>
          </div>
        </div>
      </header>

      {/* ── Body ── */}
      <div style={{ display: "flex", flex: 1, minHeight: 0 }}>

        {/* ── Left Sidebar ── */}
        <aside
          style={{
            width: sidebarHidden ? 0 : 256,
            overflow: sidebarHidden ? "hidden" : "auto",
            opacity: sidebarHidden ? 0 : 1,
            transition: "width 0.3s, opacity 0.3s",
            borderRight: "1px solid #e2e8f0",
            background: "#f8fafc",
            padding: sidebarHidden ? 0 : 16,
            display: "flex",
            flexDirection: "column",
            gap: 16,
            flexShrink: 0,
          }}
        >
          {/* Section label */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <div>
              <div style={{ fontSize: 11, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.08em" }}>Section</div>
              <div style={{ fontWeight: 600, fontSize: 14 }}>Linear Equations <span style={{ marginLeft: 6, fontSize: 11, background: "#fef9c3", color: "#a16207", padding: "1px 7px", borderRadius: 999, fontWeight: 600 }}>medium</span></div>
            </div>
            <button style={ghostBtn}><ChevronDown size={16} /></button>
          </div>

          {/* Stats card */}
          <div style={{ border: "1px solid #e2e8f0", borderRadius: 12, background: "#fff", padding: 12, display: "flex", flexDirection: "column", gap: 8 }}>
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13 }}>
              <span style={{ color: "#64748b" }}>Time Elapsed</span>
              <span style={{ fontFamily: "monospace", fontWeight: 600 }}>{showTimer ? "2:34" : "—:——"}</span>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13 }}>
              <span style={{ color: "#64748b" }}>Answered</span>
              <span style={{ fontWeight: 500 }}>5/{totalQ}</span>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13 }}>
              <span style={{ color: "#64748b" }}>Marked</span>
              <span style={{ fontWeight: 500 }}>1</span>
            </div>
            <div style={{ display: "flex", gap: 8, marginTop: 4 }}>
              <button style={{ ...outlineBtn, flex: 1, fontSize: 12 }} onClick={() => setTimerPaused((p) => !p)}>
                {timerPaused ? "Resume" : "Pause"}
              </button>
              <button style={{ ...outlineBtn, flex: 1, fontSize: 12 }} onClick={() => setShowTimer((p) => !p)}>
                {showTimer ? "Hide" : "Show"} Time
              </button>
            </div>
          </div>

          {/* Question grid */}
          <div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
              <span style={{ fontSize: 13, fontWeight: 600 }}>Questions</span>
              <span style={{ fontSize: 12, color: "#64748b" }}>{currentQ} / {totalQ}</span>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 8 }}>
              {QUESTIONS_GRID.map((num) => {
                const isCurrent = num === currentQ;
                const isAnswered = num < 6 && num !== 3;
                const isMarkedQ = num === 3;
                const isCorrectQ = num === 1 || num === 2 || num === 4;
                const isWrongQ = num === 5;

                return (
                  <button
                    key={num}
                    style={{
                      position: "relative",
                      height: 40,
                      borderRadius: 8,
                      fontSize: 13,
                      fontWeight: 500,
                      border: isCurrent ? "2px solid #0f172a" : "1px solid #e2e8f0",
                      outline: isCurrent ? "2px solid #0f172a" : "none",
                      outlineOffset: 2,
                      background: isCorrectQ ? "#dcfce7" : isWrongQ ? "#fee2e2" : isAnswered ? "rgba(15,23,42,0.08)" : "#fff",
                      color: isCorrectQ ? "#166534" : isWrongQ ? "#991b1b" : "#0f172a",
                      cursor: "pointer",
                    }}
                  >
                    {num}
                    {isMarkedQ && (
                      <span style={{ position: "absolute", top: -4, right: -4, width: 10, height: 10, background: "#f97316", borderRadius: "50%", display: "block" }} />
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Quick actions */}
          <div>
            <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>Quick actions</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              <button
                style={{ ...outlineBtn, justifyContent: "flex-start", gap: 8, width: "100%", fontSize: 13 }}
                onClick={() => setMarked((p) => !p)}
              >
                {marked ? <BookmarkCheck size={16} /> : <Bookmark size={16} />}
                {marked ? "Unmark for Review" : "Mark for Review"}
              </button>
              <button style={{ ...outlineBtn, justifyContent: "flex-start", gap: 8, width: "100%", fontSize: 13 }}>
                <Calculator size={16} /> Open Calculator
              </button>
            </div>
          </div>
        </aside>

        {/* ── Main Column ── */}
        <div style={{ flex: 1, display: "flex", flexDirection: "column", minHeight: 0 }}>

          {/* ── Split View ── */}
          <div style={{ flex: 1, display: "flex", overflow: "hidden", minHeight: 0 }}>

            {/* Left: Passage */}
            <div style={{ width: "50%", height: "100%", overflowY: "auto", borderRight: "1px solid #e2e8f0", padding: 32 }}>
              <p style={{ fontSize: 17, lineHeight: 1.75, color: "#1e293b", maxWidth: "65ch" }}>
                {DEMO_PASSAGE}
              </p>
            </div>

            {/* Divider handle */}
            <div style={{ width: 6, background: "#e2e8f0", cursor: "col-resize", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              <div style={{ width: 3, height: 32, borderRadius: 99, background: "#94a3b8" }} />
            </div>

            {/* Right: Question + Options */}
            <div style={{ width: "calc(50% - 6px)", height: "100%", overflowY: "auto", padding: 24 }}>

              {/* Question header */}
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 24 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <div style={{ width: 32, height: 32, background: "#0f172a", color: "#fff", borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, fontSize: 14 }}>
                    {currentQ}
                  </div>
                  <button
                    style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, fontWeight: 500, color: marked ? "#f97316" : "#64748b", background: "none", border: "none", cursor: "pointer" }}
                    onClick={() => setMarked((p) => !p)}
                  >
                    {marked ? <BookmarkCheck size={16} /> : <Bookmark size={16} />}
                    Mark for Review
                  </button>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <button style={{ ...ghostBtn, fontSize: 13, display: "flex", alignItems: "center", gap: 4 }}>
                    <Flag size={16} /> Report
                  </button>
                  <button style={ghostBtn}><Undo2 size={16} /></button>
                </div>
              </div>

              {/* Question text */}
              <div style={{ fontSize: 17, marginBottom: 24, lineHeight: 1.6 }}>
                {DEMO_QUESTION.question}
              </div>

              {/* Answer options */}
              <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                {DEMO_QUESTION.options.map((opt) => {
                  const isSelected = selected === opt.id;
                  const showResult = checked && isSelected;
                  const showCorrect = checked && opt.isCorrect && !isSelected;

                  let borderColor = "#e2e8f0";
                  let bgColor = "#fff";
                  let textColor = "#0f172a";
                  let circleStyle: React.CSSProperties = { border: "2px solid #0f172a", background: "#fff", color: "#0f172a" };

                  if (isSelected && !checked) {
                    borderColor = "#0f172a";
                    bgColor = "rgba(15,23,42,0.05)";
                    circleStyle = { border: "2px solid #0f172a", background: "#0f172a", color: "#fff" };
                  }
                  if (showResult && opt.isCorrect) {
                    borderColor = "#bbf7d0";
                    bgColor = "#f0fdf4";
                    textColor = "#166534";
                    circleStyle = { border: "2px solid #86efac", background: "#dcfce7", color: "#166534" };
                  }
                  if (showResult && !opt.isCorrect) {
                    borderColor = "#fecaca";
                    bgColor = "#fef2f2";
                    textColor = "#991b1b";
                    circleStyle = { border: "2px solid #fca5a5", background: "#fee2e2", color: "#991b1b" };
                  }
                  if (showCorrect) {
                    borderColor = "#bbf7d0";
                    bgColor = "rgba(240,253,244,0.8)";
                    textColor = "#166534";
                    circleStyle = { border: "2px solid #86efac", background: "#dcfce7", color: "#166534" };
                  }

                  return (
                    <button
                      key={opt.id}
                      onClick={() => !checked && setSelected(opt.id)}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 16,
                        padding: "14px 16px",
                        borderRadius: 12,
                        border: `2px solid ${borderColor}`,
                        background: bgColor,
                        color: textColor,
                        textAlign: "left",
                        cursor: checked ? "default" : "pointer",
                        width: "100%",
                        transition: "all 0.15s",
                      }}
                    >
                      <div style={{ width: 32, height: 32, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, fontSize: 14, flexShrink: 0, ...circleStyle }}>
                        {opt.id}
                      </div>
                      <span style={{ flex: 1, fontSize: 15 }}>{opt.text}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {/* ── Explanation Panel (slides up from bottom) ── */}
          {showExplanation && checked && selected && (
            <div style={{ borderTop: "1px solid #e2e8f0", background: "#fff", padding: "20px 24px", boxShadow: "0 -4px 12px rgba(0,0,0,0.06)" }}>
              <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 12 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <div style={{ width: 40, height: 40, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", background: isCorrect ? "#dcfce7" : "#fee2e2", border: `1px solid ${isCorrect ? "#bbf7d0" : "#fecaca"}` }}>
                    {isCorrect
                      ? <CheckCircle2 size={20} color="#166534" />
                      : <X size={20} color="#991b1b" />
                    }
                  </div>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 17 }}>{isCorrect ? "Correct!" : "Incorrect"}</div>
                    <div style={{ fontSize: 13, color: "#64748b" }}>
                      {isCorrect ? "Great job!" : `The correct answer is ${DEMO_QUESTION.options.find((o) => o.isCorrect)?.id}`}
                    </div>
                  </div>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <button style={{ ...outlineBtn, color: "#7c3aed", borderColor: "#ddd6fe", fontSize: 13, gap: 6 }}>
                    <Bot size={16} /> Ask Aira
                  </button>
                  <button style={ghostBtn} onClick={() => setShowExplanation(false)}><X size={20} /></button>
                </div>
              </div>
              <div style={{ background: "#f8fafc", borderRadius: 10, padding: 16 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, fontWeight: 600, marginBottom: 8 }}>
                  <Lightbulb size={16} color="#eab308" /> Explanation
                </div>
                <p style={{ fontSize: 14, color: "#334155", lineHeight: 1.6 }}>{DEMO_QUESTION.explanation}</p>
              </div>
            </div>
          )}

          {/* ── Bottom Footer ── */}
          <footer style={{ height: 64, borderTop: "1px solid #e2e8f0", display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0 16px", background: "#fff", position: "sticky", bottom: 0, zIndex: 30 }}>
            {/* Navigator button */}
            <button style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 14px", borderRadius: 8, background: "#0f172a", color: "#fff", fontWeight: 600, fontSize: 14, border: "none", cursor: "pointer" }}>
              {currentQ} of {totalQ} <ChevronDown size={16} />
            </button>

            {/* Right actions */}
            <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
              <button style={ghostBtn}><Info size={20} /></button>

              <button
                style={{ ...ghostBtn, display: "flex", alignItems: "center", gap: 6, fontSize: 14 }}
                onClick={() => setShowExplanation((p) => !p)}
              >
                <Lightbulb size={16} /> Explanation
              </button>

              <button style={{ ...ghostBtn, color: "#64748b", display: "flex", alignItems: "center", gap: 6, fontSize: 14 }}>
                <Bot size={16} /> Ask Aira ✦
              </button>

              <button
                style={{ ...ghostBtn, display: "flex", alignItems: "center", gap: 6, fontSize: 14, opacity: selected ? 1 : 0.4, cursor: selected ? "pointer" : "not-allowed" }}
                onClick={handleCheck}
                disabled={!selected}
              >
                <CheckCircle2 size={16} /> Check
              </button>

              <div style={{ display: "flex", alignItems: "center", gap: 8, paddingLeft: 12, borderLeft: "1px solid #e2e8f0", marginLeft: 4 }}>
                <button style={{ ...ghostBtn, opacity: 0.4 }}><ChevronLeft size={20} /></button>
                <button style={{ ...outlineBtn, fontSize: 14 }}>Next</button>
              </div>
            </div>
          </footer>

          {/* Progress bar */}
          <div style={{ width: "100%", height: 4, background: "rgba(0,0,0,0.08)" }}>
            <div style={{ height: "100%", background: "#0f172a", width: `${(currentQ / totalQ) * 100}%`, transition: "width 0.3s" }} />
          </div>

          {/* Disclaimer */}
          <div style={{ textAlign: "center", padding: "6px 0", fontSize: 11, color: "#94a3b8", background: "#f8fafc" }}>
            All questions are created by verified experts.
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Shared micro-styles ──
const ghostBtn: React.CSSProperties = {
  background: "none",
  border: "none",
  cursor: "pointer",
  padding: "6px 10px",
  borderRadius: 8,
  display: "flex",
  alignItems: "center",
  color: "#475569",
};

const outlineBtn: React.CSSProperties = {
  background: "#fff",
  border: "1px solid #e2e8f0",
  cursor: "pointer",
  padding: "6px 12px",
  borderRadius: 8,
  display: "flex",
  alignItems: "center",
  color: "#0f172a",
  fontWeight: 500,
};
